/**
 * Category Cockpit — the WRITES (band B of the integration plan).
 *
 * Deliberately a separate module from `categoryCockpitService.ts`, whose contract is that nothing
 * in it writes. Every function here calls an endpoint that ALREADY EXISTS and is owned by another
 * router; the cockpit adds no write of its own and duplicates no logic. Each one names, in its
 * doc comment, the route it calls and what that route does beyond the obvious — several of them
 * do more than their name suggests, and the confirmation the operator reads is built from it.
 *
 * ⛔⛔ THE ENVELOPES ARE NOT THE SAME, AND ASSUMING ONE IS HOW A FAILED WRITE READS AS DONE.
 * Measured at `origin/next` 52b41e15b, across the nine routers this module calls:
 *   - `{ success: true, data }`            agent-actions
 *   - `{ success: true, …fields }`         category-characteristics, semantic-gaps, key-proposals,
 *                                          category-granularity
 *   - `{ ok: true, scored, failed }`       kg-readiness — and `{ ok: false, … }` WITH HTTP 500 when
 *                                          scoring succeeded but the priority refresh failed
 *   - the service result, bare             discover-subcategories, fill-gaps-with-data,
 *                                          domain-knowledge — no envelope at all
 * `runAction` therefore treats a response as successful only when nothing in it says otherwise,
 * and hands the WHOLE body back so the caller can show what the server actually reported.
 *
 * Routes read at `origin/next` 52b41e15ba5f4f59591a41b1aff4f21794b78ed8 (2026-09-23).
 */

import { authFetch } from '@/utils/auth-fetch';

const CHAR = '/api/category-characteristics';
const GAPS = '/api/semantic-gaps';
const AGENT_ACTIONS = '/api/agent-actions';
const GRANULARITY = '/api/category-granularity';
const KEY_PROPOSALS = '/api/category-key-proposals';
const DOMAIN_KNOWLEDGE = '/api/domain-knowledge';
const SECTORS = '/api/sectors';
const KG_READINESS = '/api/admin/kg-readiness';

/**
 * A write that did not happen, or did not certainly happen.
 *
 * `status` is the HTTP status and `body` the server's whole reply, because several of these routes
 * put the operator-actionable part of a refusal in fields beside `error`: `proposal_changed` sends
 * `was` / `now`, the split routes send `current_status`, the per-aspect refresh sends
 * `weightsWritten` — and its ABSENCE means "unknown", which is a different thing from `false`
 * (category-characteristics.js, the note at the refusal). Nothing here flattens that to a string.
 */
/**
 * What is known about the write after a failure — and "we do not know" is one of the answers.
 *
 * ⛔ THE STATUS CODE IS NOT THIS. A 200 can be a partial application; a 500 can be a committed
 * approval whose follow-on failed; a lost body says nothing at all about work the server already
 * did. Only a 4xx is, by itself, an explicit refusal: the request was rejected before any work.
 */
export type ActionOutcome =
  /** refused by this client before anything was sent — the one guaranteed no-op */
  | 'noop'
  /** the server's answer establishes that nothing was applied */
  | 'refused'
  /** the server's answer says some of the work WAS applied */
  | 'partial'
  /** the request was sent and nothing that came back settles whether it was applied */
  | 'unknown';

/**
 * Every `sectorSync` in a reply that reports a failure, at any depth. The key is the contract every
 * persona writer shares (services/persona/sectorPersonasSync.js @ 34aaadc21); the depth is not, so
 * it is searched rather than addressed. Bounded, because a reply is data and not a promise of shape.
 */
export function failedSectorSyncs(v: unknown, depth = 0): Array<{ error: string; retry: string | null }> {
  if (!v || typeof v !== 'object' || depth > 6) return [];
  const out: Array<{ error: string; retry: string | null }> = [];
  Object.entries(v as Record<string, unknown>).forEach(([k, x]) => {
    if (k === 'sectorSync' && x && typeof x === 'object' && (x as Record<string, unknown>).status === 'failed') {
      const r = x as Record<string, unknown>;
      out.push({ error: String(r.error ?? 'no reason given'), retry: r.retry ? String(r.retry) : null });
    } else if (x && typeof x === 'object') {
      out.push(...failedSectorSyncs(x, depth + 1));
    }
  });
  return out;
}

export class ActionError extends Error {
  status: number;

  code: string;

  body: Record<string, unknown> | null;

  /**
   * ⛔⛔ WHAT HAPPENED TO THE WRITE — the whole question, in one field.
   *
   * Round 5 answered it with two booleans (`sent`, `partial`) and a special-cased `code` read in
   * `<Act>`: `code === 'no_reply'` was the ONLY way to reach "outcome unknown". That made the
   * safeguard reachable by exactly one path, so every other way of losing the answer — a body
   * that stops arriving mid-read, an empty reply, a reply that is not JSON — fell through to the
   * default and was labelled "refused by the server" over a write that may have completed.
   * A total field cannot have that shape of hole: every throw names its outcome, and `<Act>`
   * switches on it rather than recognising one code.
   */
  outcome: ActionOutcome;

  /** derived: did anything leave the browser? `false` only for `noop` — a guaranteed no-op. */
  sent: boolean;

  /** derived: the server answered and its answer says SOME of the work was done. */
  partial: boolean;

  constructor(
    message: string, status: number, code: string, body: Record<string, unknown> | null,
    opts: { sent?: boolean; partial?: boolean; outcome?: ActionOutcome } = {},
  ) {
    super(message);
    this.name = 'ActionError';
    this.status = status;
    this.code = code;
    this.body = body;
    this.outcome = opts.outcome
      ?? (opts.sent === false ? 'noop' : opts.partial === true ? 'partial' : 'refused');
    this.sent = this.outcome !== 'noop';
    this.partial = this.outcome === 'partial';
  }
}

export interface ActionResult {
  status: number;
  body: Record<string, unknown>;
  /** the server accepted the work but says it is not complete (HTTP 202, `partial: true`) */
  partial: boolean;
}

/**
 * Run one write and decide, from the reply alone, whether it happened.
 *
 * ⛔ A 200 IS NOT AN AGREEMENT THAT THE WRITE HAPPENED. `discover-subcategories` and
 * `fill-gaps-with-data` forward the service result unchanged, and that result carries its own
 * `success: false` on a refusal; `kg-readiness` answers `ok: false`; several routes answer 200 with
 * an `error` field. Each of those is a failure with a 2xx status, and the operator must see it as
 * one. So: a reply is a success only when the transport succeeded AND no field in it says it did
 * not — never because the status code was friendly.
 */
async function runAction(url: string, init: RequestInit): Promise<ActionResult> {
  let res: Response;
  try {
    res = await authFetch(url, init);
  } catch (e) {
    // ⛔ NO REPLY IS NOT A REFUSAL. The request was sent; a dropped connection, a proxy timeout or
    // a closed laptop lid loses the ANSWER, not necessarily the work — and several of these
    // endpoints spend money or destroy data before they would have answered. Reported as its own
    // outcome so the screen can say "unknown" and re-read, instead of saying "refused" and
    // inviting a retry of a write that may already have gone through.
    throw new ActionError(
      `the request was sent and no reply came back (${e instanceof Error ? e.message : String(e)}) — it may have been applied; the screen below was re-read, check it before retrying`,
      0, 'no_reply', null, { outcome: 'unknown' },
    );
  }
  // ⛔⛔ A 4xx IS THE ONLY STATUS THAT REFUSES ON ITS OWN. The request was rejected before the
  // route did any work, so an unreadable body there costs nothing. A 5xx is the opposite: the
  // handler ran, and a reply we cannot read tells us nothing about how far it got — including
  // whether it spent money or deleted a column. So everything below that cannot be read is
  // `unknown` unless the status itself already settled it.
  const refusedByStatus = res.status >= 400 && res.status < 500;
  const cannotTell = (message: string, code: string) => new ActionError(
    refusedByStatus ? message
      : `${message} — the write may have completed; the screen below was re-read, check it before retrying`,
    res.status, code, null, { outcome: refusedByStatus ? 'refused' : 'unknown' },
  );

  let body: Record<string, unknown> | null = null;
  let text: string;
  try {
    // ⛔ `authFetch` RESOLVES WHEN THE HEADERS ARRIVE, NOT WHEN THE BODY DOES. Reading the body is
    // a second trip over the same connection and it can reject on its own — after the route has
    // finished writing. Left outside this try it escaped as a plain Error, which is not an
    // ActionError at all, so `<Act>` fell through to "refused by the server": the exact claim the
    // round-5 transport catch was added to stop making, reachable by a different door.
    text = await res.text();
  } catch (e) {
    throw cannotTell(
      `HTTP ${res.status}: the reply started and then stopped (${e instanceof Error ? e.message : String(e)})`,
      'no_body',
    );
  }
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw cannotTell(`HTTP ${res.status}: the reply was not JSON — ${text.slice(0, 200)}`, 'bad_response');
    }
  }
  if (!body) throw cannotTell(`HTTP ${res.status}: the reply was empty`, 'empty_response');

  const said = (k: string) => Object.prototype.hasOwnProperty.call(body as object, k);
  const failed = !res.ok
    || (said('success') && body.success === false)
    || (said('ok') && body.ok === false)
    || (said('error') && !!body.error);
  if (failed) {
    // ⛔ A PER-ITEM `errors` ARRAY MEANS PARTLY APPLIED, AND IT ALSO CARRIES THE ONLY MESSAGE.
    // fill-gaps-with-data answers 200 `{ success: false, errors: [{gap, error}], gaps, results }`
    // with NO `error` and NO `message`, so the old line produced the string "HTTP 200" — a
    // refusal notice that named neither what failed nor that anything had succeeded, over a run
    // whose other aspects were already committed and paid for.
    const errs = Array.isArray(body.errors) ? (body.errors as unknown[]) : null;
    // ⛔⛔ A 500 THAT SAYS `approval_committed` IS NOT A REFUSAL — IT IS THE OPPOSITE.
    // hitl-approve commits the approval (children materialised, proposal promoted to `proposed`)
    // and THEN applies the split; when that second step fails unexpectedly the route deliberately
    // answers 500 with `approval_committed: true` so the state change is not hidden
    // (backend-merged/routes/category-granularity.js:192 @ 52b41e15b). Read as a plain 500 it
    // became "refused by the server" — and the obvious response to that, approving again, is the
    // one thing the route now answers 409 to, because the proposal has left `needs_hitl`.
    const committed = body.approval_committed === true;
    // `refresh/:aspect` for personas answers 500 `{ committed: true, retry }` when the category write
    // committed and the sector rebuild after it failed (routes/category-characteristics.js:613 @
    // 34aaadc21). Same shape as `approval_committed`: work is done, and it is not a refusal.
    const writeCommitted = committed || body.committed === true;

    // ⛔⛔ AND A READABLE 5xx IS NOT A REFUSAL EITHER — SAME DEFAULT, ONE DOOR FURTHER ALONG.
    // Round 6 established that only a 4xx refuses on its own, and then applied it to the branch
    // where the body could not be READ, leaving the branch where it could on `refused`. The two
    // branches answer the same question, so one of them was wrong: `kg-readiness/score` answers
    // 500 `{ ok:false, scored, refresh_failed:true }` AFTER the scores are committed
    // (backend-merged/routes/kg-readiness.js:267 @ 52b41e15b) — the scoring succeeded and the
    // view refresh did not — and it was being reported as "refused by the server".
    //
    // ⇒ Three questions, in order, and only the first two can produce a strong claim:
    //   1. does the reply DECLARE work done?          → partial
    //   2. does it DECLARE that none was done?        → refused
    //   3. otherwise, is the status one that refuses on its own (4xx), or did the handler
    //      complete and choose to report failure (2xx)? → refused; a 5xx is `unknown`.
    const COMPLETED_COUNTS = ['scored', 'applied', 'resolved', 'updated', 'written', 'created', 'removed', 'processed', 'succeeded'];
    const countSaysWork = COMPLETED_COUNTS.some((k) => typeof body[k] === 'number' && (body[k] as number) > 0);
    // `weightsWritten` is this repo's existing spelling of the same contract, and it is honoured
    // in BOTH directions: `true` is work, `false` is a definitive no-write even at a 500, and
    // ABSENT is neither (admin/src/pages/admin/__tests__/regeneratePersonasChain.test.ts @ 52b41e15b).
    // ⛔ …BUT ONLY WHEN SOMETHING IN THE LIST SUCCEEDED. The same 200 shape is also the route's
    // PREFLIGHT refusal: no product matched the category (or the scan failed), and it answers
    // `{ success: false, errors: [{ gap: 'all', error }], gaps: [], results: {} }` having written
    // and researched nothing (CategoryCharacteristicsService.js:9018 @ 891a948111). Reading every
    // non-empty `errors` as "partly applied" put PARTLY APPLIED — "work is committed, do not run
    // it again" — over a run that never started. The per-gap return lists exactly the failed gaps
    // (`failedGaps`, :9325), so work was done only when fewer gaps failed than were attempted.
    const gapList = Array.isArray(body.gaps) ? (body.gaps as unknown[]) : null;
    const allItemsFailed = !!(errs && errs.length) && (
      errs.some((x) => !!x && typeof x === 'object' && (x as Record<string, unknown>).gap === 'all')
      || (gapList !== null && errs.length >= gapList.length));
    const declaresWork = writeCommitted || body.weightsWritten === true || countSaysWork
      || (!!(errs && errs.length) && res.ok && !allItemsFailed);
    const declaresNoWrite = body.weightsWritten === false || (allItemsFailed && res.ok && !writeCommitted && !countSaysWork);
    const partial = declaresWork;
    const errText = (x: unknown) => (x && typeof x === 'object'
      ? `${String((x as Record<string, unknown>).gap ?? '?')} — ${String((x as Record<string, unknown>).error ?? 'no reason given')}`
      : String(x));
    const fromErrors = !errs || !errs.length ? ''
      // the preflight refusal is about the whole run: its reason IS the message
      : errs.length === 1 && (errs[0] as Record<string, unknown> | null)?.gap === 'all'
        ? String((errs[0] as Record<string, unknown>).error ?? 'no reason given')
        : `${errs.length} of ${gapList ? gapList.length : errs.length} failed: ${errs.map(errText).slice(0, 6).join(' · ')}`;
    const served = String(body.error || body.message || fromErrors || `HTTP ${res.status}`);
    const detail = body.detail ? ` — ${String(body.detail)}` : '';
    const message = committed
      ? `the approval WAS committed and only the split application failed${detail}. Do not approve it again — the route answers 409 once the proposal has left needs_hitl; remediate the now-proposed proposal through dry-run/apply.`
      : body.committed === true
        ? `${served}. The write IS committed — do not run it again${body.retry ? `; re-run only the failed step: ${String(body.retry)}` : ''}.`
        : served;
    const code = String(body.code || body.error || (partial ? 'partly_applied' : `http_${res.status}`));
    const outcome: ActionOutcome = declaresWork ? 'partial'
      : declaresNoWrite ? 'refused'
        // a 4xx was rejected before the handler did anything; a 2xx means the handler ran to
        // completion and reported its own failure. Everything left is a 5xx we can read and
        // cannot interpret — the handler ran, and how far it got is not in the reply.
        : (refusedByStatus || res.ok) ? 'refused'
          : 'unknown';
    throw new ActionError(
      outcome === 'unknown'
        ? `${message} — the server failed part-way and its reply does not say how far it got; the write may have completed, so the screen below was re-read, check it before retrying`
        : message,
      res.status, code, body, { outcome },
    );
  }
  // ⛔ 202 IS NOT THE ONLY WAY A ROUTE SAYS "NOT FINISHED". research-extraction-hints answers
  // HTTP 200 with `status: 'processing'` and does the research and the re-enrichment behind the
  // reply, so reading the status code alone put a "done" heading over work that was still running
  // and still spending. The three words below are the ones these routes actually use; a route that
  // reports completion carries no `status` at all, so an unknown value is read as unfinished
  // rather than as finished.
  // ⛔⛔ A 200 THAT CARRIES A FAILED SECTOR REBUILD IS PARTLY APPLIED, WHEREVER IN THE REPLY IT SITS.
  // Every writer of a category's personas re-runs the sector_personas projection AFTER its own
  // commit, and a failed projection is returned as `sectorSync: { status: 'failed', error, retry }`
  // rather than thrown (services/persona/sectorPersonasSync.js:44-53 @ 34aaadc21) — so the reply says
  // `success: true`. Leaf retirement puts it at the top level (:6164-6167); an agent action puts it
  // inside `executionResult`. Read per endpoint it was missed per endpoint: the retirement read
  // "done" while the sector's personas stayed stale and the only recovery instruction sat in
  // collapsed JSON (codex-reviewer, round 14). One detector, at the one place every reply passes.
  const syncFailures = failedSectorSyncs(body);
  if (syncFailures.length) {
    const f = syncFailures[0];
    throw new ActionError(
      `the write IS committed, but the sector_personas rebuild that follows it failed: ${f.error}. The sector's personas are stale until it is re-run — do not repeat the write${f.retry ? `; re-run only the rebuild: ${f.retry}` : ''}.`,
      res.status, 'sector_sync_failed', body, { outcome: 'partial' },
    );
  }
  const async = typeof body.status === 'string' && ['processing', 'queued', 'pending'].includes(body.status);
  return { status: res.status, body, partial: res.status === 202 || body.partial === true || async };
}

const post = (url: string, json?: unknown): Promise<ActionResult> => runAction(url, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(json ?? {}),
});

/** paths carry ' > ', '&' and '/', and every one of these routes takes the path as a URL SEGMENT */
const seg = (path: string) => encodeURIComponent(path);

// ============================================================================
// The 10 aspects
// ============================================================================

/**
 * `POST /api/category-characteristics/:sectorId/category/:path/refresh` — research all 10 aspects.
 *
 * Super-admin only, synchronous, and it pays: ten model calls plus a separate attribute-intelligence
 * run. The reply carries `accounting`, whose `status` is one of `recorded`, `no_calls`, `not_run`,
 * `research_failed` or `persist_failed` — the last meaning the money was spent and the row does not
 * carry it. That is not an error the route reports as one, so the cockpit shows `accounting` itself.
 */
export function researchAllAspects(sectorId: number, path: string): Promise<ActionResult> {
  return post(`${CHAR}/${sectorId}/category/${seg(path)}/refresh`);
}

/**
 * `POST …/refresh/:aspect` — re-research ONE aspect.
 *
 * `expectedUpdatedAt` is the row revision the screen displayed. The route compares it against the
 * row it is about to overwrite and refuses (409) when they differ, BEFORE any paid call — so a
 * refresh launched from a stale screen costs nothing instead of silently discarding whatever
 * happened in between. The reply carries the new revision as `updatedAt`, and, for `personas`,
 * `benefitWeightsReplaced`: the curated vectors this run destroyed.
 */
export function refreshAspect(sectorId: number, path: string, aspect: string, expectedUpdatedAt: string | null): Promise<ActionResult> {
  return post(`${CHAR}/${sectorId}/category/${seg(path)}/refresh/${encodeURIComponent(aspect)}`, expectedUpdatedAt ? { expectedUpdatedAt } : {});
}

/**
 * `POST …/discover-subcategories` with `dryRun` — what a split WOULD propose, writing nothing.
 *
 * `autoResearch` is never sent: it would research each discovered child, which is ten model calls
 * per child and no confirmation the operator ever saw.
 */
export function discoverSubcategories(sectorId: number, path: string): Promise<ActionResult> {
  return post(`${CHAR}/${sectorId}/category/${seg(path)}/discover-subcategories`, { dryRun: true });
}

/** `POST …/fill-gaps-with-data` — scan the site's products of this category, then research the gaps. Paid. */
export function fillGapsWithData(sectorId: number, path: string, siteId: string, forceRefresh = false): Promise<ActionResult> {
  return post(`${CHAR}/${sectorId}/category/${seg(path)}/fill-gaps-with-data`, { siteId, forceRefresh });
}

/**
 * `POST /api/category-characteristics/:sectorId/mark-stale` — mark THIS path stale.
 *
 * ⛔⛔ THE ROUTE IS SECTOR-WIDE WHEN THE FILTER IS EMPTY. Its own SQL branches on
 * `categoryPaths && categoryPaths.length > 0`; without it the UPDATE is `WHERE sector_id = $1` and
 * every category of the sector becomes stale. So the filter is not optional here, and the guard
 * below is not defensive style: it is the difference between one row and ninety-seven. A caller
 * that reaches this with an empty list has a bug, and it must not become a sector-wide write.
 */
export async function markStale(sectorId: number, paths: string[]): Promise<ActionResult> {
  if (!paths.length) {
    throw new ActionError(
      'refusing to call mark-stale with no category: the route treats an empty filter as "the whole sector"',
      0, 'empty_filter_refused', null, { sent: false },
    );
  }
  return post(`${CHAR}/${sectorId}/mark-stale`, { categoryPaths: paths });
}

/**
 * `POST …/chat` with `execute: false` — parse a sentence into an operation, write nothing.
 *
 * The reply's `parsed` is `{ operation, conceptType, conceptName, context, confirmationMessage }`.
 * Executing it is band D; this is the read half, and the route enforces the difference.
 */
export function parseSentence(sectorId: number, path: string, message: string): Promise<ActionResult> {
  return post(`${CHAR}/${sectorId}/category/${seg(path)}/chat`, { message, execute: false });
}

/**
 * `POST …/chat` with `draft: true` — band D. The sentence is parsed and checked exactly as an
 * execute would be, then filed as a PENDING agent_actions row under `siteId` (which must belong to
 * the sector). Nothing reaches the category until a super admin approves it in To decide. The parse
 * is one model call, paid whether or not a draft comes out of it.
 */
export function draftSentence(sectorId: number, path: string, message: string, siteId: string, from: 'chat' | 'judge' = 'chat'): Promise<ActionResult> {
  return post(`${CHAR}/${sectorId}/category/${seg(path)}/chat`, { message, draft: true, siteId, draftFrom: from });
}

/**
 * `POST /api/category-cockpit/sectors/:sectorId/category/aspects/:aspect/draft?path=` — a JSON edit
 * of one aspect, filed as a pending `replace_aspect` draft. `base` is the value the edit started
 * from: an edit made against a value that has since changed is refused (409 stale_base).
 */
export function draftAspect(
  sectorId: number, path: string, aspect: string,
  body: { siteId: string; data: unknown; base: unknown; note: string },
): Promise<ActionResult> {
  return post(`/api/category-cockpit/sectors/${sectorId}/category/aspects/${encodeURIComponent(aspect)}/draft?path=${encodeURIComponent(path)}`, body);
}

// ============================================================================
// Leaf retirement — bare-leaf duplicates only
// ============================================================================

/**
 * `GET /:sectorId/leaf-retirement/preview` — the verdict for every BARE-LEAF row of the sector.
 *
 * The rows it returns are those whose `category_path` holds no ' > '. A full path is not retirable
 * by this route at all (it answers 400), so the cockpit offers the button only for a bare leaf and
 * shows this preview's verdict — DELETABLE, REFUSED or NO_TWIN — before offering the delete.
 */
export function leafRetirementPreview(sectorId: number): Promise<ActionResult> {
  return runAction(`${CHAR}/${sectorId}/leaf-retirement/preview`, { method: 'GET' });
}

/**
 * The state the preview approved, exactly as `leafPreviewRow` returned it. All three fields or
 * none: the route validates the object as a unit and answers 400 for a partial one, because a
 * partial expectation checked partially is worse than no expectation at all.
 */
export interface LeafExpectation {
  leaf_id: string;
  full_path_id: string;
  /** 64 hex characters — the digest of the rows and the loss set the operator was shown */
  approval_digest: string;
}

/**
 * `DELETE /:sectorId/category/:leaf/leaf` — merge the bare leaf into its full-path twin and delete it.
 *
 * ⛔ THE EXPECTATION IS NOT OPTIONAL HERE. The route accepts a call without `expected` — a hand
 * call may not have a preview — and then deletes whatever it finds under the locks. The cockpit
 * ALWAYS has a preview, because the button is disabled until one has been read, so sending nothing
 * would discard a guarantee the screen already holds: between the preview and the click the twin
 * can be re-researched, and the row would still be deleted while the verdict on screen still reads
 * DELETABLE. With the expectation the route recomputes the digest under both FOR UPDATE locks and
 * answers `412 PRECONDITION_FAILED` with the differing field, having written nothing.
 */
// `async` so the refusal below is a REJECTION and not a synchronous throw: `<Act>` calls this
// inside its try, but every other caller in this module rejects, and one function that throws
// where its siblings reject is the kind of difference a caller discovers in production.
export async function retireLeaf(sectorId: number, leaf: string, expected: LeafExpectation): Promise<ActionResult> {
  const ok = expected
    && String(expected.leaf_id || '').trim() !== ''
    && String(expected.full_path_id || '').trim() !== ''
    && /^[0-9a-f]{64}$/.test(String(expected.approval_digest || ''));
  if (!ok) {
    // Fail closed rather than fall back to an unchecked delete: an incomplete expectation means the
    // preview this screen is holding is not the one the route can verify, and the whole point of
    // the button being gated on a preview is that the delete is checked against it.
    throw new ActionError(
      'refusing to retire without the preview’s approved state (leaf_id, full_path_id and the 64-hex approval_digest): re-read the preview and try again',
      0, 'no_expectation', null, { sent: false },
    );
  }
  return runAction(`${CHAR}/${sectorId}/category/${seg(leaf)}/leaf`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expected }),
  });
}

// ============================================================================
// Semantic gaps
// ============================================================================

/**
 * `PUT /api/semantic-gaps/:id/review` — one gap.
 *
 * `decision` is one of `approve | modify | reject` and nothing else: `SemanticGapService.reviewGap`
 * validates the list and throws on anything outside it. `modify` REQUIRES `finalValue` — the
 * service refuses it without one — and writes status `modified`, which Apply accepts exactly as it
 * accepts `approved`.
 */
export function reviewGap(gapId: string, decision: 'approve' | 'modify' | 'reject', notes?: string, finalValue?: string): Promise<ActionResult> {
  return runAction(`${GAPS}/${encodeURIComponent(gapId)}/review`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision, ...(notes ? { notes } : {}), ...(finalValue ? { finalValue } : {}) }),
  });
}

/** `POST /api/semantic-gaps/bulk-review` — the same decision over a named list of gap ids. */
export async function bulkReviewGaps(gapIds: string[], decision: string): Promise<ActionResult> {
  if (!gapIds.length) throw new ActionError('refusing a bulk review with no gap', 0, 'empty_selection_refused', null, { sent: false });
  return post(`${GAPS}/bulk-review`, { gapIds, decision });
}

/**
 * `POST /api/semantic-gaps/apply` — write the approved mappings of ONE category into
 * `category_characteristics` and record a `category_research_event`.
 *
 * Apply refuses rows whose suggester is unknown or a small model, so `applied` can be smaller than
 * the approved count; the reply carries the real number and the cockpit reports that, not its own.
 */
export function applyGapsForCategory(sectorId: number, categoryPath: string): Promise<ActionResult> {
  return post(`${GAPS}/apply`, { sectorId, categoryPath });
}

/** `POST /api/semantic-gaps/apply-all/:siteId` — the same, for every category of one site. */
export function applyGapsForSite(siteId: string): Promise<ActionResult> {
  return post(`${GAPS}/apply-all/${encodeURIComponent(siteId)}`);
}

// ============================================================================
// The agent-action inbox
// ============================================================================

/**
 * `POST /api/agent-actions/:id/execute`.
 *
 * ⛔ THE ROUTE ANSWERS 200 FOR A HANDLER THAT FAILED. `AgentActionService.executeAction` marks the
 * action executed and returns the row whatever the handler reported, so `success: true` here means
 * "the action was run", never "the action worked". The truth is in `data.execution_result`, and
 * `executedAgentAction` below reads it — a caller that only checked the envelope would tell the
 * operator a re-enrichment succeeded while its handler threw.
 */
export function executeAgentAction(id: string): Promise<ActionResult> {
  return post(`${AGENT_ACTIONS}/${encodeURIComponent(id)}/execute`);
}

/**
 * What the handler said, pulled out of an execute reply. `null` when the row carries no result at
 * all — which is itself worth showing, because it means nothing recorded what happened.
 */
export function handlerOutcome(r: ActionResult): { ok: boolean | null; detail: string } {
  const data = (r.body.data || {}) as Record<string, unknown>;
  // ⛔⛔ THE FIELD IS `executionResult`, AND READING `execution_result` HERE MADE EVERY FAILED
  // HANDLER LOOK INCONCLUSIVE UNDER A GREEN HEADING — the exact outcome this function exists to
  // prevent. `AgentActionService.executeAction` returns `{ success, action, executionResult }` and
  // the route answers `{ success: true, data: <that> }`, so `data.execution_result` is undefined
  // for every reply, success and failure alike, and the screen said "nothing conclusive" either way.
  //
  // The second reading is the SAME value, not a fallback to a different meaning: `action` is the
  // row re-read by `getAction(actionId)` after the UPDATE that wrote `execution_result` from this
  // very object. It is read second so a reply that carries the row but not the top-level field —
  // an older deployment, a route that forwards only the row — is still answered from the database's
  // copy rather than from nothing.
  const act = (data.action || {}) as Record<string, unknown>;
  const raw = data.executionResult ?? act.execution_result;
  if (raw == null) return { ok: null, detail: 'the action is marked executed and the reply carries no executionResult' };
  const er = (typeof raw === 'string' ? safeParse(raw) : raw) as Record<string, unknown> | null;
  if (!er || typeof er !== 'object') return { ok: null, detail: String(raw).slice(0, 400) };
  const ok = er.success === undefined ? null : er.success !== false;
  const detail = String(er.error || er.message || JSON.stringify(er)).slice(0, 600);
  return { ok, detail };
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/** `POST /api/agent-actions/:id/dismiss` — the reason is stored on the row. */
export function dismissAgentAction(id: string, reason: string): Promise<ActionResult> {
  return post(`${AGENT_ACTIONS}/${encodeURIComponent(id)}/dismiss`, { reason });
}

// ============================================================================
// Split proposals
// ============================================================================

/**
 * `POST /api/category-granularity/:siteId/hitl-approve` — approve or reject a split.
 *
 * The only decision in the cockpit that resumes a process by itself: approving re-classifies the
 * category's products. HTTP 202 with `partial: true` means the decision was recorded and the work
 * behind it was not finished, which `ActionResult.partial` carries.
 */
export function decideSplit(siteId: string, proposalId: string, decision: 'approve_split' | 'reject_split', notes?: string): Promise<ActionResult> {
  return post(`${GRANULARITY}/${encodeURIComponent(siteId)}/hitl-approve`, {
    proposalId, decision, ...(notes ? { reviewer_notes: notes } : {}),
  });
}

/**
 * `POST /api/category-granularity/:siteId/proposals/:id/dismiss` — close a proposal without splitting.
 *
 * The route refuses a reason shorter than three characters (400 `reason_required`): the reason is
 * the audit of why the gate was released, so the confirmation asks for it rather than sending a
 * placeholder.
 */
export function dismissSplit(siteId: string, proposalId: string, reason: string): Promise<ActionResult> {
  return post(`${GRANULARITY}/${encodeURIComponent(siteId)}/proposals/${encodeURIComponent(proposalId)}/dismiss`, { reason });
}

// ============================================================================
// Catalogue key proposals
// ============================================================================

/**
 * `POST /api/category-key-proposals/:id/accept`.
 *
 * `seen_proposed_key` is the key the SCREEN showed. The route compares it with the stored one under
 * a row lock and answers 409 `proposal_changed` with `was` / `now` when they differ — so an accept
 * can never confirm a key the operator did not read. Sending nothing is refused by the route itself.
 */
export function acceptKeyProposal(id: number, seenProposedKey: string): Promise<ActionResult> {
  return post(`${KEY_PROPOSALS}/${id}/accept`, { seen_proposed_key: seenProposedKey });
}

/** `POST /api/category-key-proposals/:id/reject` — final for an accepted row (409). */
export function rejectKeyProposal(id: number): Promise<ActionResult> {
  return post(`${KEY_PROPOSALS}/${id}/reject`);
}

// ============================================================================
// Knowledge notes
// ============================================================================

/** `POST /api/domain-knowledge/:id/approve`. */
export function approveKnowledgeNote(id: string): Promise<ActionResult> {
  return post(`${DOMAIN_KNOWLEDGE}/${encodeURIComponent(id)}/approve`);
}

/** `POST /api/domain-knowledge/:id/deprecate` — the route requires a reason. */
export function deprecateKnowledgeNote(id: string, reason: string): Promise<ActionResult> {
  return post(`${DOMAIN_KNOWLEDGE}/${encodeURIComponent(id)}/deprecate`, { reason });
}

// ============================================================================
// Extraction hints and readiness
// ============================================================================

/**
 * `POST /api/sectors/:sectorId/categories/:categoryId/research-extraction-hints`.
 *
 * ⛔ IT DOES TWO THINGS AND ITS NAME SAYS ONE. Besides researching the hints it RE-ENRICHES this
 * category's catalogue rows against them, which may submit enrichment batch jobs and cost provider
 * spend — the route's own message says so, after an operator ran it mid-measurement and had 35
 * products submitted unannounced. The confirmation in the cockpit says both.
 *
 * `categoryId` is a row id, not a path: the route resolves it against `sector_category_templates`
 * first and `category_characteristics` second. The cockpit sends `category_characteristics.id`,
 * which the category payload carries for exactly this call.
 *
 * The reply is `status: 'processing'` — the work runs after the response. `truncated` with
 * `dropped_attributes` means some attributes were never asked about, which is indistinguishable
 * from a negative result once it is in the cache, so the cockpit shows them.
 */
export function researchExtractionHints(sectorId: number, categoryId: number | string, siteId: string, forceRegenerate = false): Promise<ActionResult> {
  return post(`${SECTORS}/${sectorId}/categories/${encodeURIComponent(String(categoryId))}/research-extraction-hints`, { siteId, forceRegenerate });
}

/**
 * `POST /api/admin/kg-readiness/score/:siteId` — re-score the site's categories now.
 *
 * Answers `{ ok: true, scored, failed }`, or HTTP 500 with `ok: false` when the scoring itself
 * worked but the priority-view refresh or the stale cleanup did not. That second case is a real
 * failure with real counts in it, and `runAction` raises it with the whole body attached.
 */
export function scoreReadiness(siteId: string): Promise<ActionResult> {
  return post(`${KG_READINESS}/score/${encodeURIComponent(siteId)}`);
}
